import {
  getAnnotationTrackingConditioning,
  type AnnotationRequestOptions,
  type AnnotationTrackingConditioningView,
} from "@/api";
import type { TrackingKeyframeSeed } from "./trackingProtocol";

export const MAX_TRACKING_CONDITIONING_KEYFRAMES = 128;

export type RegistryTrackingConditioning = Readonly<{
  setRevision: number;
  keyframes: readonly TrackingKeyframeSeed[];
}>;

type FetchConditioning = (
  setUuid: string,
  camera: string,
  options: AnnotationRequestOptions,
) => Promise<AnnotationTrackingConditioningView>;

export class TrackingConditioningCache {
  private readonly cache = new Map<string, RegistryTrackingConditioning>();
  private readonly inflight = new Map<string, Promise<RegistryTrackingConditioning>>();

  constructor(
    private readonly fetchConditioning: FetchConditioning = getAnnotationTrackingConditioning,
  ) {}

  async load(
    request: { setUuid: string; setRevision: number; camera: string },
    { signal }: { signal: AbortSignal },
  ): Promise<RegistryTrackingConditioning> {
    const key = JSON.stringify([request.setUuid, request.setRevision, request.camera]);
    const cached = this.cache.get(key);
    if (cached !== undefined) return cached;
    const pending = this.inflight.get(key) ?? this.fetch(request, signal);
    this.inflight.set(key, pending);
    try {
      return await pending;
    } finally {
      if (this.inflight.get(key) === pending) this.inflight.delete(key);
    }
  }

  clear(): void {
    this.cache.clear();
    this.inflight.clear();
  }

  private async fetch(
    request: { setUuid: string; setRevision: number; camera: string },
    signal: AbortSignal,
  ): Promise<RegistryTrackingConditioning> {
    const response = await this.fetchConditioning(request.setUuid, request.camera, { signal });
    if (response.set_uuid !== request.setUuid || response.set_revision !== request.setRevision) {
      throw new Error("annotation tracking conditioning revision mismatch");
    }
    if (response.keyframes.length > MAX_TRACKING_CONDITIONING_KEYFRAMES) {
      throw new Error(
        `Tracking conditioning keyframe limit exceeded (${response.keyframes.length}/${MAX_TRACKING_CONDITIONING_KEYFRAMES})`,
      );
    }
    const keyframes = response.keyframes.map((frame) => ({
      camera: frame.camera,
      episode: frame.episode,
      frameIndex: frame.frame_index,
      objects: frame.targets.map((target) => ({
        obj_id: target.obj_id,
        points: target.points,
        labels: target.labels,
      })),
    }));
    if (
      keyframes.length === 0 ||
      !keyframes.some((frame) => frame.objects.some((object) => object.labels.includes(1)))
    ) {
      throw new Error("Tracking conditioning requires at least one include point");
    }
    const result = { setRevision: response.set_revision, keyframes };
    this.cache.set(JSON.stringify([request.setUuid, request.setRevision, request.camera]), result);
    return result;
  }
}
